import React, { useState, useEffect } from 'react';
import { BarChart2, Users, Clock, TrendingUp, Activity } from 'lucide-react';
import { patients, sessions, behaviors } from '../services/api';

const Analytics = () => {
  const [stats, setStats] = useState({
    totalPatients: 0,
    activeSessions: 0,
    behaviorIncidents: 0,
    progressRate: 0
  });
  const [behaviorData, setBehaviorData] = useState([]);
  const [sessionData, setSessionData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);
        console.log('Fetching analytics data...');

        // Fetch patients
        const patientsResponse = await patients.getAll();
        console.log('Patients response:', patientsResponse);
        const activePatients = patientsResponse.data.data.filter(p => p.status === 'active');

        // Fetch sessions
        const sessionsResponse = await sessions.getAll();
        console.log('Sessions response:', sessionsResponse);
        const activeSessions = sessionsResponse.data.filter(s => s.status === 'scheduled');
        setSessionData(sessionsResponse.data);

        // Fetch behaviors
        try {
          const behaviorsResponse = await behaviors.getAll();
          console.log('Behaviors response:', behaviorsResponse);
          setBehaviorData(behaviorsResponse.data);
          
          // Calculate stats with behavior data
          const progressRate = calculateProgressRate(behaviorsResponse.data);
          setStats({
            totalPatients: activePatients.length,
            activeSessions: activeSessions.length,
            behaviorIncidents: behaviorsResponse.data.length,
            progressRate: progressRate
          });
        } catch (behaviorError) {
          console.error('Error fetching behaviors:', behaviorError);
          // Set stats without behavior data
          setStats({
            totalPatients: activePatients.length,
            activeSessions: activeSessions.length,
            behaviorIncidents: 0,
            progressRate: 0
          });
        }

      } catch (error) {
        console.error('Error fetching analytics data:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  const calculateProgressRate = (behaviors) => {
    if (!behaviors?.length) return 0;
    const improvingBehaviors = behaviors.filter(b => b.status === 'Improving').length;
    return Math.round((improvingBehaviors / behaviors.length) * 100);
  };

  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 mr-3">
          <BarChart2 size={20} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analíticos</h1>
          <p className="text-gray-600">Acompanhar e analisar o progresso do paciente</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Total de Pacientes</h3>
            <Users className="text-brand-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.totalPatients}</p>
          <p className="text-sm text-green-600 mt-2">+12% do mês passado</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Sessões Ativas</h3>
            <Clock className="text-brand-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.activeSessions}</p>
          <p className="text-sm text-green-600 mt-2">+8% do mês passado</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Taxa de Progresso</h3>
            <TrendingUp className="text-brand-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.progressRate}%</p>
          <p className="text-sm text-green-600 mt-2">+5% do mês passado</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Objetivos Comportamentais</h3>
            <Activity className="text-brand-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-gray-900">89%</p>
          <p className="text-sm text-green-600 mt-2">+3% do mês passado</p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Visão Geral do Progresso do Paciente</h3>
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
            <p className="text-gray-500">Espaço reservado para gráfico - Os dados de progresso do paciente serão exibidos aqui</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Frequência às Sessões</h3>
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
            <p className="text-gray-500">Espaço reservado para gráfico - Os dados de frequência às sessões serão exibidos aqui</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Tendências de Acompanhamento Comportamental</h3>
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
            <p className="text-gray-500">Espaço reservado para gráfico - As tendências de acompanhamento comportamental serão exibidas aqui</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Conquista dos Objetivos Terapêuticos</h3>
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
            <p className="text-gray-500">Espaço reservado para gráfico - Os dados de conquista dos objetivos terapêuticos serão exibidos aqui</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 